import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

import { messages } from '../../../../_shared/i18n/messages';

import styles from './Toolbar.module.scss';

const propTypes = {

	/**
	 * The toolbar ID.
	 */
	id: PropTypes.string.isRequired,

	/**
	 * The editor ID.
	 */
	editorDomId: PropTypes.string.isRequired,

	/**
	 * The menu buttons to render.
	 */
	children: PropTypes.arrayOf(PropTypes.node),

	/**
	 * Callback function to handle events when the toolbar loses focus.
	 */
	onBlur: PropTypes.func,

};

/**
 * The WYSIWYG toolbar for the ProseMirror TextEditor component.
 *
 * https://www.w3.org/WAI/ARIA/apg/patterns/toolbar
 *
 * @param {object} props  The toolbar props.
 *
 * @returns {node} result  The toolbar component.
 */
const Toolbar = (props) => {

	const {
		id,
		editorDomId,
		children,
		onBlur,
		...restProps
	} = props;

	const intl = useIntl();

	const [rovingIndex, setRovingIndex] = useState(0);

	const handleOnKeyDown = (event, currentIndex) => {

		const { key } = event;

		const buttons = document.querySelectorAll(`#${id} button`);

		if (!buttons || buttons.length === 0) {
			return;
		}

		const firstIndex = 0;
		const lastIndex = buttons.length - 1;

		let indexToFocus = -1;

		switch (key) {

		// Focus on next button (don't wrap around)
		case 'ArrowRight':

			event.preventDefault();

			if (currentIndex < lastIndex) {

				// Find the next available button that is not disabled
				for (let i = currentIndex + 1; i <= lastIndex; i++) {

					const button = buttons[i];

					if (button && !button.disabled) {
						indexToFocus = i;
						break;
					}

				}

			}

			break;

		// Focus on previous button (don't wrap around)
		case 'ArrowLeft':

			event.preventDefault();

			if (currentIndex > firstIndex) {

				// Find the previous available button that is not disabled
				for (let i = currentIndex - 1; i >= firstIndex; i--) {

					const button = buttons[i];

					if (button && !button.disabled) {
						indexToFocus = i;
						break;
					}

				}

			}

			break;


		// Jump to the first button
		case 'Home':

			event.preventDefault();

			// Find the first button that is not disabled
			for (let i = 0; i <= lastIndex; i++) {

				const button = buttons[i];

				if (button && !button.disabled) {
					indexToFocus = i;
					break;
				}

			}

			break;

		// Jump to the last button
		case 'End':

			event.preventDefault();

			// Find the last button that is not disabled
			for (let i = lastIndex; i >= firstIndex; i--) {

				const button = buttons[i];

				if (button && !button.disabled) {
					indexToFocus = i;
					break;
				}

			}
			break;

		// Blur the toolbar
		case 'Tab':
		case 'Shift-Tab':
		case 'Escape':

			if (onBlur) {
				onBlur();
			}

			break;

		// Ignore all other key strokes
		default:
			return;
		}

		if (indexToFocus >= 0 &&
			indexToFocus <= lastIndex &&
			buttons[indexToFocus]) {

			const buttonToFocus = buttons[indexToFocus];

			setRovingIndex(indexToFocus);

			buttonToFocus.focus();

		}

	};

	return (
		<div
			{...restProps}
			id={id}
			aria-controls={editorDomId}
			aria-label={intl.formatMessage(
				messages.TC_COMPONENTS_TEXT_EDITOR_TOOLBAR_TITLE
			)}
			role="toolbar"
			// Merge, don't replace: the parent's className (e.g.
			// tc-text-editor-toolbar, which TextEditorInline hides until editing)
			// was kept by react-css-modules' styleName and must be kept here too.
			className={`${restProps.className || ''} ${styles.toolbar}`.trim()}>
			{React.Children.map(children, (child, index) => (
				React.cloneElement(child, {
					a11yToolbarProps: {
						tabIndex: index === rovingIndex ? '0' : '-1',
						onKeyDown: (event) => handleOnKeyDown(event, index),
					}
				})
			))}
		</div>
	);

};

Toolbar.propTypes = propTypes;

export default Toolbar;
